import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useLanguage } from '../../context/LanguageContext';
import CategoryBadge from './CategoryBadge';
import LazyImage from '../ui/LazyImage';
import { AuthorByline } from './AuthorSocialLinks';
import { generateSlug } from '../../utils/generateSlug';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const ArticleBodyLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const ArticleCard = styled.article`
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.colors.card};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 20px;
  overflow: hidden;
  box-shadow: ${props => props.theme.shadows.subtle};
  transition: box-shadow 0.25s ease, transform 0.25s ease;
  height: 100%;
  animation: ${fadeIn} 0.4s ease-out forwards;
  animation-delay: ${props => props.index * 60}ms;
  
  &:hover {
    box-shadow: ${props => props.theme.shadows.soft};
    transform: translateY(-2px);
  }
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 7.5rem;
  overflow: hidden;
  flex-shrink: 0;
  
  @media (min-width: 640px) {
    height: 9rem;
  }
  
  @media (min-width: 768px) {
    height: 10rem;
  }
  
  @media (min-width: 1024px) {
    height: 11rem;
  }
  
  @media (min-width: 1440px) {
    height: 12rem;
  }
  
  @media (min-width: 1920px) {
    height: 13rem;
  }
`;

const BadgeContainer = styled.div`
  position: absolute;
  top: 1rem;
  left: 1rem;
  
  @media (min-width: 640px) {
    top: 1.125rem;
    left: 1.125rem;
  }
  
  @media (min-width: 1024px) {
    top: 1.25rem;
    left: 1.25rem;
  }
  
  @media (min-width: 1440px) {
    top: 1.5rem;
    left: 1.5rem;
  }
  
  @media (min-width: 1920px) {
    top: 2rem;
    left: 2rem;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 0.9rem 1rem 0.95rem;
  min-height: 0;
  
  @media (min-width: 640px) {
    padding: 1rem 1.1rem 1.05rem;
  }
  
  @media (min-width: 768px) {
    padding: 1.1rem 1.2rem 1.1rem;
  }
  
  @media (min-width: 1024px) {
    padding: 1.2rem 1.25rem 1.15rem;
  }
  
  @media (min-width: 1440px) {
    padding: 1.3rem 1.35rem 1.2rem;
  }
  
  @media (min-width: 1920px) {
    padding: 2rem;
  }
`;

const MetaInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: ${props => props.theme.colors.mutedForeground};
  margin-bottom: 0.6rem;
  
  @media (min-width: 640px) {
    font-size: 0.8rem;
    margin-bottom: 0.7rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 0.85rem;
    margin-bottom: 0.75rem;
  }
  
  @media (min-width: 1440px) {
    font-size: 1rem;
    margin-bottom: 1.25rem;
  }
  
  @media (min-width: 1920px) {
    font-size: 1.125rem;
    margin-bottom: 1.5rem;
  }
`;

const Title = styled.h3`
  font-family: ${props => props.theme.typography.fonts.display};
  font-size: 1rem;
  font-weight: ${props => props.theme.typography.fontWeights.semibold};
  line-height: 1.4;
  margin-bottom: 0.55rem;
  color: ${props => props.theme.colors.cardForeground};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  transition: color 0.3s;
  
  @media (min-width: 640px) {
    font-size: 1.05rem;
    margin-bottom: 0.65rem;
  }
  
  @media (min-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 0.7rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 1.15rem;
    margin-bottom: 0.75rem;
  }
  
  @media (min-width: 1440px) {
    font-size: 1.2rem;
    margin-bottom: 0.8rem;
  }
  
  @media (min-width: 1920px) {
    font-size: 2rem;
    margin-bottom: 1.5rem;
  }
  
  ${ArticleCard}:hover & {
    color: ${props => props.theme.colors.foreground};
  }
`;

const Excerpt = styled.p`
  font-size: 0.85rem;
  color: ${props => props.theme.colors.mutedForeground};
  line-height: 1.75;
  margin-bottom: 0.7rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  flex: 1;
  
  @media (min-width: 640px) {
    font-size: 0.9rem;
    margin-bottom: 0.8rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 0.95rem;
    margin-bottom: 0.85rem;
  }
  
  @media (min-width: 1440px) {
    font-size: 1rem;
    margin-bottom: 0.9rem;
  }
  
  @media (min-width: 1920px) {
    font-size: 1.375rem;
    margin-bottom: 1.75rem;
  }
`;

const AuthorSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0 1rem 1rem;
  border-top: 1px solid ${props => props.theme.colors.border};
  margin-top: auto;
  
  @media (min-width: 640px) {
    padding-top: 1.125rem;
    gap: 0.875rem;
  }
  
  @media (min-width: 1024px) {
    padding-top: 1.25rem;
    gap: 1rem;
  }
  
  @media (min-width: 1440px) {
    padding-top: 1.5rem;
    gap: 1.25rem;
  }
  
  @media (min-width: 1920px) {
    padding-top: 2rem;
    gap: 1.5rem;
  }
`;

const PostCard = ({
  title,
  excerpt,
  category,
  categoryColor,
  author,
  authorSlug,
  authorAvatar,
  date,
  readTime,
  imageUrl,
  slug,
  index = 0,
}) => {
  const { getArticleUrl } = useLanguage();
  const articleSlug = slug || generateSlug(title);
  const articleUrl = getArticleUrl(articleSlug);

  return (
    <ArticleCard index={index}>
      <ArticleBodyLink to={articleUrl}>
        <ImageContainer>
          <LazyImage src={imageUrl} alt={title} />
          <BadgeContainer>
            <CategoryBadge category={category} color={categoryColor} />
          </BadgeContainer>
        </ImageContainer>

        <Content>
          <MetaInfo>
            <span>{date}</span>
            <span>•</span>
            <span>{readTime}</span>
          </MetaInfo>

          <Title>{title}</Title>

          <Excerpt>{excerpt}</Excerpt>
        </Content>
      </ArticleBodyLink>

      <AuthorSection>
        <AuthorByline name={author} slug={authorSlug} avatar={authorAvatar} />
      </AuthorSection>
    </ArticleCard>
  );
};

export default PostCard;
